import { Box, Card, CardContent, CardHeader, IconButton, Stack } from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import {
  FLOW_FILTER_TYPE_SUPPORTED_OPS,
  FlowCaseSensitivity,
  FlowFieldDataType,
  FlowFilterOperation,
} from 'flow-schema';

import { colors, isDefined } from 'common';

import { BlockValueTemplateInnerField } from './CodeMirror/FlowTemplate';
import { BlockRadioGroupField } from './RadioGroupField';
import { BlockInnerSelectField } from './SelectField';
import { useSelectedNodeField } from './useSelectedNodeField';

const FILTER_OPERATION_NAMES: Record<FlowFilterOperation, string> = {
  [FlowFilterOperation.Equals]: 'equals',
  [FlowFilterOperation.NotEquals]: 'does not equal',
  [FlowFilterOperation.Contains]: 'contains',
  [FlowFilterOperation.DoesNotContain]: 'does not contain',
  [FlowFilterOperation.GreaterThan]: 'is greater than',
  [FlowFilterOperation.GreaterThanOrEqualTo]: 'is greater than or equal to',
  [FlowFilterOperation.LessThan]: 'is less than',
  [FlowFilterOperation.LessThanOrEqualTo]: 'is less than or equal to',
  [FlowFilterOperation.IsOneOf]: 'is one of',
  [FlowFilterOperation.IsNotOneOf]: 'is not one of',
  [FlowFilterOperation.StartsWith]: 'starts with',
  [FlowFilterOperation.EndsWith]: 'ends with',
  [FlowFilterOperation.IsNull]: 'is null',
  [FlowFilterOperation.IsNotNull]: 'is not null',
};

const FILTER_NULL_OPERATIONS = new Set([FlowFilterOperation.IsNull, FlowFilterOperation.IsNotNull]);

const OPERATION_OPTIONS_BY_DATATYPE = Object.fromEntries(
  Object.entries(FLOW_FILTER_TYPE_SUPPORTED_OPS).map(
    ([dataType, supportedOps]: [string, Set<FlowFilterOperation>]) => {
      const options = Object.values(FlowFilterOperation)
        .filter((entry) => supportedOps.has(entry))
        .map((entry) => ({
          label: FILTER_OPERATION_NAMES[entry],
          value: entry,
        }));
      return [dataType, options];
    }
  )
);

interface FilterConditionCardProps {
  filterConditionsFieldNamePrefix: string;
  supportedDataTypes: FlowFieldDataType[];
  onDelete: () => void;
  fieldsToFilter?: string[];
}

export function FilterConditionCard(props: FilterConditionCardProps) {
  const { filterConditionsFieldNamePrefix, supportedDataTypes, onDelete, fieldsToFilter } = props;
  const { value: operation, setValue: setOperation } = useSelectedNodeField<FlowFilterOperation>(
    `${filterConditionsFieldNamePrefix}.operation`
  );
  const { value: dataType } = useSelectedNodeField<FlowFieldDataType>(
    `${filterConditionsFieldNamePrefix}.dataType`
  );
  const availableOperations = isDefined(dataType) ? OPERATION_OPTIONS_BY_DATATYPE[dataType] : [];
  const isNullOperation = isDefined(operation) && FILTER_NULL_OPERATIONS.has(operation);
  const isFieldToFilterSelectable = isDefined(fieldsToFilter) && fieldsToFilter.length > 0;

  return (
    <Card variant="outlined">
      <CardHeader
        sx={{ backgroundColor: colors.background.default, padding: 1 }}
        action={
          <IconButton aria-label="Delete Filter Condition" size="medium" onClick={onDelete}>
            <XIcon />
          </IconButton>
        }
      />
      <CardContent>
        <Stack gap={1} direction="row">
          <Stack gap={1} width="100%">
            {isFieldToFilterSelectable ? (
              <BlockInnerSelectField
                fieldName={`${filterConditionsFieldNamePrefix}.fieldToFilter`}
                clearable={false}
                options={fieldsToFilter.map((column) => ({ label: column, value: column })) || []}
                aria-label="Field to Filter"
              />
            ) : (
              <BlockValueTemplateInnerField
                fieldId={`value-${filterConditionsFieldNamePrefix}`}
                fieldName={`${filterConditionsFieldNamePrefix}.fieldToFilter`}
                aria-label="Field to Filter"
                isMultiline
              />
            )}
            <Stack direction="row">
              <Box width="35%" bgcolor={colors.extended.gray[93]} color={colors.extended.gray[45]}>
                <BlockInnerSelectField
                  fieldName={`${filterConditionsFieldNamePrefix}.dataType`}
                  options={Object.values(supportedDataTypes).map((dataType) => ({
                    label: dataType,
                    value: dataType,
                  }))}
                  aria-label="Data Type"
                  clearable={false}
                  borderRadius="4px 0 0 4px"
                  onChange={(event) => {
                    const newDataType = event.target.value as FlowFieldDataType;
                    const newOperations = isDefined(newDataType)
                      ? OPERATION_OPTIONS_BY_DATATYPE[newDataType]
                      : [];
                    if (newOperations.length > 0) {
                      setOperation(newOperations[0].value);
                    }
                  }}
                />
              </Box>
              <Box width="65%">
                <BlockInnerSelectField
                  fieldName={`${filterConditionsFieldNamePrefix}.operation`}
                  options={availableOperations}
                  aria-label="Operation"
                  clearable={false}
                  borderRadius="0 4px 4px 0"
                />
              </Box>
            </Stack>
            {!isNullOperation && (
              <BlockValueTemplateInnerField
                fieldId={`value-${filterConditionsFieldNamePrefix}`}
                fieldName={`${filterConditionsFieldNamePrefix}.value`}
                aria-label="Value"
                isMultiline
              />
            )}
            {dataType === FlowFieldDataType.String && !isNullOperation && (
              <Stack direction="row" gap={1} alignItems="center">
                <BlockRadioGroupField
                  fieldName={`${filterConditionsFieldNamePrefix}.caseSensitivity`}
                  options={Object.values(FlowCaseSensitivity).map((caseSensitivity) => ({
                    label: caseSensitivity,
                    value: caseSensitivity,
                  }))}
                  aria-label="Match Rule"
                  label=""
                  row
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
